import { useRef, useState } from "react";
import { MapPin, Eye, Play } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PORTFOLIO } from "@/data/content";

const PortfolioCard = ({ item, index }) => {
  const videoRef = useRef(null);
  const [hover, setHover] = useState(false);
  const alwaysPlay = !!item.autoplay;
  const videoVisible = alwaysPlay || hover;

  const onEnter = () => {
    setHover(true);
    if (!alwaysPlay) videoRef.current?.play().catch(() => {});
  };
  const onLeave = () => {
    setHover(false);
    if (!alwaysPlay) videoRef.current?.pause();
  };

  const Wrapper = item.link ? "a" : "div";
  const wrapperProps = item.link ? { href: item.link, target: "_blank", rel: "noopener noreferrer" } : {};

  return (
    <Reveal delay={(index % 3) * 0.12}>
      <Wrapper
        {...wrapperProps}
        data-testid={`portfolio-item-${index}`}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        className="group relative block rounded-2xl overflow-hidden h-80 cursor-pointer"
      >
        <img
          src={item.image}
          alt={item.title}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-[transform,opacity] duration-700 ${videoVisible ? "opacity-0" : "opacity-100 group-hover:scale-105"}`}
        />
        <video
          ref={videoRef}
          src={item.video}
          poster={item.image}
          muted
          loop
          playsInline
          autoPlay={alwaysPlay}
          preload={alwaysPlay ? "auto" : "none"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoVisible ? "opacity-100" : "opacity-0"}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
        <div className="absolute inset-0 border border-white/10 rounded-2xl group-hover:border-indigo-400/50 group-hover:shadow-[0_0_40px_rgba(99,102,241,0.25)] transition-[border-color,box-shadow] duration-500" />

        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-400 ${hover ? "opacity-0" : "opacity-100"}`}>
          <span className="w-14 h-14 rounded-full glass flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" />
          </span>
        </div>

        <div className="absolute top-4 left-4">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white btn-gradient px-3 py-1.5 rounded-full">{item.category}</span>
        </div>

        <div className="absolute bottom-0 inset-x-0 p-5">
          <h3 className="font-display text-lg font-bold text-white leading-snug">{item.title}</h3>
          <div className="mt-2 flex items-center justify-between text-xs text-zinc-400">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-indigo-400" />{item.location}</span>
            <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-purple-400" />{item.metrics}</span>
          </div>
        </div>
      </Wrapper>
    </Reveal>
  );
};

const Portfolio = () => (
  <section id="portfolio" data-testid="portfolio-section" className="relative py-24 sm:py-32 overflow-hidden">
    <div className="glow-blob w-[28rem] h-[28rem] bg-blue-600/10 bottom-0 -left-40" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        caption="Our Campaigns"
        title={<>Moments we've made <span className="text-gradient">the internet replay.</span></>}
        description="Street interviews, public reactions, product launches, giveaways, crowd engagement and brand promotions — hover any card to preview the action."
        testId="portfolio-heading"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PORTFOLIO.map((item, i) => (
          <PortfolioCard key={item.title} item={item} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Portfolio;
